import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import cuentaCorrienteService from '../../services/cuentaCorrienteService';
import companiaService from '../../services/companiaService';
import FacturaPOSClientePanel from '../facturaventa/pos/FacturaPOSClientePanel';
import FacturaPOSLineasGrid from '../facturaventa/pos/FacturaPOSLineasGrid';
import FacturaPOSSearchBar, { type SuggestionItem } from '../facturaventa/pos/FacturaPOSSearchBar';
import FacturaPOSTotalesPanel, { type POSCompaniaOption } from '../facturaventa/pos/FacturaPOSTotalesPanel';
import { POSCliente, POSLinea } from '../facturaventa/pos/types';
import ProveedorSelectorField, { type ProveedorSelectorValue } from '../../components/ProveedorSelectorField';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import {
  filtrarSucursalesPorCompania,
  resolveNroSucParaCompania,
} from '../../utils/sucursalPreferida';
import { persistGlobalIdCompania } from '../../utils/companiaPreferida';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatCurrency } from '../../utils/formatters';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';
import type { Proveedor } from '../../services/proveedorService';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = l.cantidad * l.precioUnitario;
  return Math.round(base * (1 - l.descuento / 100) * 100) / 100;
};

const lineaVacia = (): POSLinea => ({
  id: genId(),
  codigoArticulo: '',
  descripcionArticulo: '',
  cantidad: 1,
  precioUnitario: 0,
  descuento: 0,
  subtotal: 0,
  um: '',
});

interface SucursalVenta {
  nroSuc: number;
  nombreSucursal?: string;
  idCompania?: string | null;
}

export type ComprobanteInternoPosTipo = '80' | '81';
export type ComprobanteInternoPosModo = 'clientes' | 'proveedores';

interface Props {
  modo: ComprobanteInternoPosModo;
  tipo: ComprobanteInternoPosTipo;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

/** Solo artículos financieros en crédito/débito interno. */
const TIPOS_ARTICULO_INTERNO = 'FIN';

const NuevaComprobanteInternoPOS: React.FC<Props> = ({ modo, tipo }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { preferidaNro } = useSucursalesUsuarioContext();

  const backPath =
    modo === 'clientes' ? '/ventas/movimientos-internos' : '/compras/movimientos-internos';
  const esCredito = tipo === '80';
  const titulo = esCredito ? 'Crédito interno' : 'Débito interno';

  const nroClienteQuery = Number(searchParams.get('nroCliente'));
  const nroClientePrecargado =
    Number.isFinite(nroClienteQuery) && nroClienteQuery > 0 ? nroClienteQuery : 0;

  const [posCliente, setPosCliente] = useState<POSCliente | null>(null);
  const [proveedor, setProveedor] = useState<ProveedorSelectorValue | null>(null);
  const [lineas, setLineas] = useState<POSLinea[]>([lineaVacia()]);
  const [fechaEmision, setFechaEmision] = useState(todayIso());
  const [fechaVencim, setFechaVencim] = useState(todayIso());
  const [observaciones, setObservaciones] = useState('');
  const [nroSuc, setNroSuc] = useState(0);
  const [idCompania, setIdCompania] = useState('');
  const [saving, setSaving] = useState(false);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSuc, setLoadingSuc] = useState(false);
  const [companias, setCompanias] = useState<POSCompaniaOption[]>([]);
  const [loadingCompanias, setLoadingCompanias] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cargar = async () => {
      setLoadingSuc(true);
      setLoadingCompanias(true);
      try {
        const [resSuc, listaCompanias] = await Promise.all([
          api.get('/api/SucursalesVenta'),
          companiaService.getCompanias({ pageNumber: 1, pageSize: 100 }),
        ]);
        if (cancelled) return;
        const items = (resSuc.data?.items ?? resSuc.data ?? []) as SucursalVenta[];
        setSucursales(Array.isArray(items) ? items : []);
        const lista = Array.isArray(listaCompanias) ? listaCompanias : [];
        const comps = lista.map((c) => ({
          idCompania: String(c.idCompania ?? '').trim(),
          nombre: c.nombre ?? undefined,
          condIva: c.condIva ?? undefined,
        }));
        setCompanias(comps.filter((c) => c.idCompania));
        const firstComp = comps.find((c) => c.idCompania)?.idCompania ?? '';
        setIdCompania((prev) => prev || firstComp);
      } catch {
        if (!cancelled) enqueueSnackbar('No se pudieron cargar sucursales/compañías', { variant: 'error' });
      } finally {
        if (!cancelled) {
          setLoadingSuc(false);
          setLoadingCompanias(false);
        }
      }
    };
    void cargar();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const sucursalesDeCompania = useMemo(
    () => filtrarSucursalesPorCompania(sucursales, idCompania),
    [sucursales, idCompania],
  );

  useEffect(() => {
    if (nroSuc > 0) return;
    const resolved = resolveNroSucParaCompania({
      idCompania,
      sucursales: sucursalesDeCompania,
      preferidaNro,
    });
    if (resolved != null && resolved > 0) setNroSuc(resolved);
  }, [sucursalesDeCompania, preferidaNro, idCompania, nroSuc]);

  useEffect(() => {
    if (modo !== 'clientes' || nroClientePrecargado <= 0 || posCliente) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/Clientes/${nroClientePrecargado}`);
        const c = res.data as {
          nroCliente?: number;
          nombre?: string;
          razonSocial?: string;
          cuit?: string;
          tipoIva?: string;
          condVta?: string;
          vendedor?: number;
          nroLista?: number | string;
        };
        if (cancelled || !c?.nroCliente) return;
        setPosCliente({
          nroCliente: c.nroCliente,
          nombre: c.nombre ?? '',
          razonSocial: c.razonSocial,
          cuit: c.cuit,
          tipoIva: c.tipoIva,
          condVta: c.condVta,
          nroVendedor: c.vendedor ?? undefined,
          nroLista: c.nroLista != null ? String(c.nroLista) : undefined,
        });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modo, nroClientePrecargado, posCliente]);

  const handleLineaChange = useCallback((id: string, field: keyof POSLinea, value: string | number) => {
    setLineas((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  }, []);

  const handleLineaFilled = useCallback((id: string, data: Partial<POSLinea>) => {
    setLineas((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  }, []);

  const handleEliminarLinea = useCallback((id: string) => {
    setLineas((prev) => {
      const next = prev.filter((l) => l.id !== id);
      return next.length ? next : [lineaVacia()];
    });
  }, []);

  const handleAgregarLineaVacia = useCallback(() => {
    setLineas((prev) => [...prev, lineaVacia()]);
  }, []);

  const handleAgregarProducto = useCallback((item: SuggestionItem) => {
    const cantidad = item.cantidad && item.cantidad > 0 ? item.cantidad : 1;
    const precio = Number(item.precioUnitario) || 0;
    const um = String(item.um ?? '').trim();
    setLineas((prev) => {
      const vaciaIdx = prev.findIndex((l) => !String(l.codigoArticulo ?? '').trim());
      const nueva: POSLinea = {
        id: genId(),
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad,
        precioUnitario: precio,
        descuento: 0,
        subtotal: calcularSubtotal({ cantidad, precioUnitario: precio, descuento: 0 }),
        um,
        stockDisponible: item.stockDisponible ?? null,
      };
      if (vaciaIdx >= 0) {
        const next = [...prev];
        next[vaciaIdx] = { ...nueva, id: prev[vaciaIdx].id };
        return next;
      }
      return [...prev, nueva];
    });
  }, []);

  const handleDuplicarLinea = useCallback((id: string) => {
    setLineas((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  }, []);

  const lineasValidas = useMemo(
    () => lineas.filter((l) => l.codigoArticulo.trim() && l.cantidad > 0),
    [lineas],
  );
  const total = useMemo(
    () => Math.round(lineasValidas.reduce((acc, l) => acc + l.subtotal, 0) * 100) / 100,
    [lineasValidas],
  );

  const terceroListo =
    modo === 'clientes' ? Boolean(posCliente?.nroCliente) : Boolean(proveedor?.nroProveedor);

  const handleGuardar = async () => {
    if (!terceroListo) {
      enqueueSnackbar(modo === 'clientes' ? 'Seleccioná un cliente' : 'Seleccioná un proveedor', {
        variant: 'warning',
      });
      return;
    }
    if (nroSuc <= 0) {
      enqueueSnackbar('Seleccioná una sucursal', { variant: 'warning' });
      return;
    }
    if (!fechaEmision || !fechaVencim) {
      enqueueSnackbar('Completá las fechas', { variant: 'warning' });
      return;
    }
    if (lineasValidas.length === 0) {
      enqueueSnackbar('Agregá al menos un artículo con cantidad', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const created = await cuentaCorrienteService.createComprobanteInterno({
        tipoCliente: modo === 'clientes' ? 'C' : 'P',
        tipo,
        nroTercero: modo === 'clientes' ? posCliente!.nroCliente : proveedor!.nroProveedor,
        nroSuc,
        fechaEmision,
        fechaVencimiento: fechaVencim,
        moneda: 0,
        items: lineasValidas.map((l) => ({
          codigoArticulo: l.codigoArticulo.trim(),
          descripcion: l.descripcionArticulo?.trim() || null,
          cantidad: l.cantidad,
          precioUnitario: l.precioUnitario,
        })),
        referencia: observaciones.trim() || null,
      });

      enqueueSnackbar(
        `${titulo} X-${String(created.nroSuc).padStart(4, '0')}-${String(created.nroComprobante ?? 0).padStart(8, '0')} · Total ${formatCurrency(created.importeTotal ?? total)}`,
        { variant: 'success' },
      );
      navigate(backPath);
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo guardar el comprobante interno'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const clienteParaPanel: POSCliente | null =
    modo === 'clientes'
      ? posCliente
      : proveedor
        ? {
            nroCliente: proveedor.nroProveedor,
            nombre: proveedor.nombre ?? '',
            cuit: proveedor.cuit,
            tipoIva: proveedor.tipoIva,
          }
        : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(backPath)}
              sx={{ textTransform: 'none' }}
            >
              Movimientos internos
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {titulo}
            </Typography>
          </Stack>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                    {modo === 'clientes' ? 'Cliente' : 'Proveedor'}
                  </Typography>
                  {modo === 'clientes' ? (
                    <FacturaPOSClientePanel
                      cliente={posCliente}
                      lineas={lineas}
                      onClienteSelect={setPosCliente}
                      onClienteClear={() => setPosCliente(null)}
                    />
                  ) : (
                    <ProveedorSelectorField
                      value={proveedor}
                      onSelect={(p: Proveedor) =>
                        setProveedor({
                          nroProveedor: p.nroProveedor,
                          nombre: p.nombre,
                          cuit: p.cuit,
                          tipoIva: p.tipoIva,
                        })
                      }
                      onClear={() => setProveedor(null)}
                      required
                    />
                  )}
                </Box>

                <Box>
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                    Artículos financieros (FIN)
                  </Typography>
                  <FacturaPOSSearchBar
                    nroLista={obtenerCodigoListaCliente(posCliente) || undefined}
                    contextoUm="factura"
                    tiposArticulo={TIPOS_ARTICULO_INTERNO}
                    placeholder="Buscar artículo financiero… código o descripción"
                    onAgregarProducto={handleAgregarProducto}
                  />
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" mt={1} mb={0.75}>
                    {lineasValidas.length > 0 && (
                      <Chip
                        label={`${lineasValidas.length} renglón${lineasValidas.length > 1 ? 'es' : ''}`}
                        size="small"
                      />
                    )}
                  </Stack>
                  <FacturaPOSLineasGrid
                    lineas={lineas}
                    nroLista={obtenerCodigoListaCliente(posCliente) || undefined}
                    fechaEmision={fechaEmision}
                    contextoUm="factura"
                    tiposArticulo={TIPOS_ARTICULO_INTERNO}
                    onLineaChange={handleLineaChange}
                    onLineaFilled={handleLineaFilled}
                    onEliminarLinea={handleEliminarLinea}
                    onDuplicarLinea={handleDuplicarLinea}
                    onAgregarLineaVacia={handleAgregarLineaVacia}
                    emptyHint="Buscá un artículo financiero (FIN) arriba o por código en la grilla"
                  />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                <FacturaPOSTotalesPanel
                  subtotalNeto={total}
                  porcDesc={0}
                  porcDesc2={0}
                  porcDesc3={0}
                  onPorcDescChange={() => {}}
                  impuestos={[]}
                  montoIVA={0}
                  total={total}
                  observaciones={observaciones}
                  onObservacionesChange={setObservaciones}
                  cliente={clienteParaPanel}
                  fechaEmision={fechaEmision}
                  onFechaEmisionChange={setFechaEmision}
                  fechaVencim={fechaVencim}
                  onFechaVencimChange={setFechaVencim}
                  idCompania={idCompania}
                  onIdCompaniaChange={(id) => {
                    setIdCompania(id);
                    persistGlobalIdCompania(id);
                    const nextSuc = resolveNroSucParaCompania({
                      idCompania: id,
                      sucursales: filtrarSucursalesPorCompania(sucursales, id),
                      preferidaNro,
                    });
                    if (nextSuc != null) setNroSuc(nextSuc);
                  }}
                  companias={companias}
                  loadingCompanias={loadingCompanias}
                  nroVendedor={posCliente?.nroVendedor ?? 0}
                  onNroVendedorChange={(nro, nombre) => {
                    setPosCliente((prev) => {
                      if (!prev) return null;
                      if (!nro) return { ...prev, nroVendedor: undefined, vendedorInfo: undefined };
                      return {
                        ...prev,
                        nroVendedor: nro,
                        vendedorInfo: { nroVendedor: nro, nombreVendedor: nombre },
                      };
                    });
                  }}
                  codigoLista={obtenerCodigoListaCliente(posCliente) || ''}
                  ordenCompra=""
                  onOrdenCompraChange={() => {}}
                  transporte=""
                  onTransporteChange={() => {}}
                  ubicacion=""
                  onUbicacionChange={() => {}}
                  saving={saving}
                  onGuardar={() => {
                    void handleGuardar();
                  }}
                  cantLineas={lineasValidas.length}
                  hideDraftButton
                  hideEntregaLogistica
                  saveLabel={`Guardar ${titulo}`}
                  sucursales={sucursalesDeCompania}
                  nroSuc={nroSuc}
                  onNroSucChange={setNroSuc}
                  loadingSucursales={loadingSuc}
                  emisionNombre={clienteParaPanel?.nombre ?? ''}
                  onEmisionNombreChange={() => {}}
                  emisionCuit={clienteParaPanel?.cuit ?? ''}
                  onEmisionCuitChange={() => {}}
                  emisionTipoIva="N"
                  onEmisionTipoIvaChange={() => {}}
                  emisionTipoIvaLocked
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NuevaComprobanteInternoPOS;
